import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface DashboardMetrics {
  draftPipeline: number;
  forecastContract: number;
  committedCost: number;
  recognizedRevenue: number;
  actualCost: number;
}

export const useDashboardMetrics = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["dashboard_metrics", user?.id],
    queryFn: async (): Promise<DashboardMetrics> => {
      // Forecast rollup
      const { data: rollup } = await supabase
        .from("v_project_forecast_rollup")
        .select("job_orders_draft_total, forecast_contract_value, committed_cost");

      const draftPipeline = rollup?.reduce((s, r) => s + (r.job_orders_draft_total ?? 0), 0) ?? 0;
      const forecastContract = rollup?.reduce((s, r) => s + (r.forecast_contract_value ?? 0), 0) ?? 0;
      const committedCost = rollup?.reduce((s, r) => s + (r.committed_cost ?? 0), 0) ?? 0;

      // Recognized revenue: posted credit lines to account 4000
      // Recognized revenue: paginate to avoid 1000-row silent truncation
      let recognizedRevenue = 0;
      let revPage = 0;
      const REV_PAGE_SIZE = 1000;
      while (true) {
        const { data: revLines } = await supabase
          .from("accounting_lines")
          .select("amount, transaction_id")
          .eq("account_code", "4000")
          .eq("entry_type", "credit")
          .range(revPage * REV_PAGE_SIZE, (revPage + 1) * REV_PAGE_SIZE - 1);

        if (!revLines || revLines.length === 0) break;

        const txnIds = [...new Set(revLines.map((l) => l.transaction_id))];
        const { data: txns } = await supabase
          .from("accounting_transactions")
          .select("id")
          .in("id", txnIds)
          .eq("status", "posted");

        const postedIds = new Set(txns?.map((t) => t.id) ?? []);
        recognizedRevenue += revLines
          .filter((l) => postedIds.has(l.transaction_id))
          .reduce((s, l) => s + l.amount, 0);

        if (revLines.length < REV_PAGE_SIZE) break;
        revPage++;
      }

      // Actual Cost: server-side aggregate of approved project_expenses
      const { data: actualCostVal, error: acErr } = await supabase.rpc("get_company_actual_cost");
      if (acErr) throw acErr;
      const actualCost = Number(actualCostVal ?? 0);

      return { draftPipeline, forecastContract, committedCost, recognizedRevenue, actualCost };
    },
    enabled: !!user,
  });
};
